import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Container,
  TextField,
  Typography,
} from "@mui/material";

import {
  clearForm,
  setEmail,
  setIsSubmitted,
  setMessage,
  setName,
  setPhone,
} from "../features/contactSlice";

import { AddContct } from "../utils/lookupUtil";
import Swal from "sweetalert2";
import {
  useLocation,
  useNavigate,
} from "react-router-dom";

const fieldStyles = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#ffffff",

    /*
      מסיר את מסגרת הפוקוס הנוספת
      שמגיעה מהדפדפן.
    */
    "& input:focus": {
      outline: "none",
      boxShadow: "none",
    },

    "& textarea:focus": {
      outline: "none",
      boxShadow: "none",
    },

    /*
      בזמן פוקוס נשארת רק מסגרת השדה הרגילה.
      אין מסגרת נוספת מסביב.
    */
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
  borderWidth: "2px",
  borderColor: "#D81B60",
},

    /*
      בשגיאה המסגרת נשארת אדומה.
    */
    "&.Mui-error .MuiOutlinedInput-notchedOutline": {
      borderColor: "#d32f2f",
    },

    "&.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline": {
      borderColor: "#d32f2f",
    },
  },
};

export default function ContactForm() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    name,
    email,
    phone,
    message,
    isSubmitted,
  } = useSelector(
    (state) => state.contact
  );

  const {
    loggedUser,
    isLoggedIn,
  } = useSelector(
    (state) => state.user
  );

  const [errors, setErrors] =
    useState({});

  const [
    submissionError,
    setSubmissionError,
  ] = useState("");

  const [isSubmitting, setIsSubmitting] =
    useState(false);

  const requireLogin = async () => {
    if (isLoggedIn) {
      return false;
    }

    await Swal.fire({
      icon: "info",
      title: "צריך להתחבר כדי לשלוח פנייה",
      text: "העברה לעמוד התחברות או הרשמה",
      confirmButtonText: "הבנתי",
      confirmButtonColor: "#D81B60",
      iconColor: "#58A76B",
      customClass: {
        popup: "custom-swal",
      },
    });

    navigate("/signin", {
      state: {
        from: location.pathname,
      },
    });

    return true;
  };

  useEffect(() => {
    if (!isLoggedIn || !loggedUser) {
      return;
    }

    if (!name) {
      dispatch(
        setName(
          loggedUser.name || ""
        )
      );
    }

    if (!email) {
      dispatch(
        setEmail(
          loggedUser.email || ""
        )
      );
    }

    if (!phone) {
      dispatch(
        setPhone(
          loggedUser.phone || ""
        )
      );
    }
  }, [
    isLoggedIn,
    loggedUser,
    name,
    email,
    phone,
    dispatch,
  ]);

  const clearFieldError = (
    fieldName
  ) => {
    setErrors(
      (currentErrors) => {
        if (
          !currentErrors[fieldName]
        ) {
          return currentErrors;
        }

        const updatedErrors = {
          ...currentErrors,
        };

        delete updatedErrors[
          fieldName
        ];

        return updatedErrors;
      }
    );
  };

  const validate = () => {
    const newErrors = {};

    const trimmedName =
      name?.trim() || "";

    const trimmedEmail =
      email?.trim() || "";

    const trimmedPhone =
      phone?.trim() || "";

    const trimmedMessage =
      message?.trim() || "";

    if (!trimmedName) {
      newErrors.name =
        "שם הוא שדה חובה";
    }

    if (!trimmedEmail) {
      newErrors.email =
        "אימייל הוא שדה חובה";
    } else if (
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
        trimmedEmail
      )
    ) {
      newErrors.email =
        "יש להזין כתובת אימייל תקינה";
    }

    if (!trimmedPhone) {
      newErrors.phone =
        "טלפון הוא שדה חובה";
    } else if (
      !/^[0-9]{9,10}$/.test(
        trimmedPhone
      )
    ) {
      newErrors.phone =
        "יש להזין מספר טלפון בן 9 או 10 ספרות";
    }

    if (!trimmedMessage) {
      newErrors.message =
        "הודעה היא שדה חובה";
    }

    return newErrors;
  };

  const focusFirstInvalidField = (
    validationErrors
  ) => {
    const firstInvalidField =
      Object.keys(
        validationErrors
      )[0];

    if (!firstInvalidField) {
      return;
    }

    const element =
      document.getElementById(
        `contact-${firstInvalidField}`
      );

    element?.focus();
  };

  const handleSubmit = async (
    event
  ) => {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    const mustLogin =
      await requireLogin();

    if (mustLogin) {
      return;
    }

    const validationErrors =
      validate();

    if (
      Object.keys(
        validationErrors
      ).length > 0
    ) {
      setErrors(
        validationErrors
      );

      setSubmissionError("");

      focusFirstInvalidField(
        validationErrors
      );

      return;
    }

    setIsSubmitting(true);
    setSubmissionError("");

    try {
      const response =
        await AddContct({
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim(),
          massages:
            message.trim(),
        });

      if (
        response?.status === 200 ||
        response?.status === 201
      ) {
        dispatch(clearForm());

        dispatch(
          setIsSubmitted(true)
        );

        setErrors({});

        await Swal.fire({
          title:
            "פנייתך נשלחה!",
          icon: "success",
          confirmButtonText:
            "אישור",
          confirmButtonColor:
            "#D81B60",
        });

        return;
      }

      setSubmissionError(
        "לא הצלחנו לשלוח את הפנייה. נסי שוב."
      );
    } catch (error) {
      console.error(
        "שגיאה בשליחת הפנייה:",
        error
      );

      setSubmissionError(
        "אירעה שגיאה בשליחת הפנייה. נסי שוב מאוחר יותר."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    dispatch(
      setIsSubmitted(false)
    );
  };

  return (
    <Container
      component="main"
      maxWidth="sm"
      dir="rtl"
      sx={{
        py: {
          xs: 3,
          md: 5,
        },

        /*
          מונע מסגרת חיצונית נוספת
          סביב שדות וכפתורים.
        */
        "& input:focus, & textarea:focus":
          {
            outline: "none",
          },
      }}
    >
      {isSubmitted ? (
        <Box
          component="section"
          aria-labelledby="contact-success-title"
          textAlign="center"
          mt={4}
        >
          <Alert
            severity="success"
            role="status"
            aria-live="polite"
          >
            <Typography
              id="contact-success-title"
              component="p"
            >
              הפנייה נשלחה בהצלחה!
            </Typography>
          </Alert>

          <Button
            type="button"
            variant="contained"
            onClick={handleBack}
            sx={{
              mt: 2,
              backgroundColor:
                "#D81B60",

              "&:hover": {
                backgroundColor:
                  "#ad1457",
              },

              "&:focus-visible": {
                outline:
                  "3px solid #000000",
                outlineOffset:
                  "3px",
              },
            }}
          >
            חזרה לטופס
          </Button>
        </Box>
      ) : (
        <Box
          component="section"
          aria-labelledby="contact-title"
        >
          <Typography
            id="contact-title"
            component="h1"
            variant="h4"
            align="center"
            gutterBottom
          >
            צור קשר
          </Typography>

          <Typography
            component="p"
            align="center"
            sx={{
              mb: 3,
            }}
          >
            השדות המסומנים בכוכבית
            הם שדות חובה.
          </Typography>

          <Box
            component="form"
            noValidate
            onSubmit={handleSubmit}
            aria-describedby={
              submissionError
                ? "contact-submission-error"
                : undefined
            }
          >
            <Box mb={2}>
              <TextField
                id="contact-name"
                name="name"
                fullWidth
                label="שם"
                variant="outlined"
                value={name}
                required
                autoComplete="name"
                error={
                  Boolean(
                    errors.name
                  )
                }
                helperText={
                  errors.name || " "
                }
                inputProps={{
                  "aria-invalid":
                    Boolean(
                      errors.name
                    ),

                  "aria-describedby":
                    errors.name
                      ? "contact-name-helper-text"
                      : undefined,
                }}
                onFocus={() => {
                  requireLogin();
                }}
                onChange={(
                  event
                ) => {
                  if (
                    !isLoggedIn
                  ) {
                    requireLogin();
                    return;
                  }

                  dispatch(
                    setName(
                      event.target
                        .value
                    )
                  );

                  clearFieldError(
                    "name"
                  );
                }}
                sx={fieldStyles}
              />
            </Box>

            <Box mb={2}>
              <TextField
                id="contact-email"
                name="email"
                type="email"
                fullWidth
                label="אימייל"
                variant="outlined"
                value={email}
                required
                autoComplete="email"
                error={
                  Boolean(
                    errors.email
                  )
                }
                helperText={
                  errors.email || " "
                }
                inputProps={{
                  inputMode:
                    "email",

                  "aria-invalid":
                    Boolean(
                      errors.email
                    ),

                  "aria-describedby":
                    errors.email
                      ? "contact-email-helper-text"
                      : undefined,
                }}
                onChange={(
                  event
                ) => {
                  dispatch(
                    setEmail(
                      event.target
                        .value
                    )
                  );

                  clearFieldError(
                    "email"
                  );
                }}
                sx={fieldStyles}
              />
            </Box>

            <Box mb={2}>
              <TextField
                id="contact-phone"
                name="phone"
                type="tel"
                fullWidth
                label="טלפון"
                variant="outlined"
                value={phone}
                required
                autoComplete="tel"
                error={
                  Boolean(
                    errors.phone
                  )
                }
                helperText={
                  errors.phone || " "
                }
                inputProps={{
                  inputMode:
                    "numeric",
                  maxLength: 10,

                  "aria-invalid":
                    Boolean(
                      errors.phone
                    ),

                  "aria-describedby":
                    errors.phone
                      ? "contact-phone-helper-text"
                      : undefined,
                }}
                onChange={(
                  event
                ) => {
                  const digitsOnly =
                    event.target.value.replace(
                      /\D/g,
                      ""
                    );

                  dispatch(
                    setPhone(
                      digitsOnly
                    )
                  );

                  clearFieldError(
                    "phone"
                  );
                }}
                sx={fieldStyles}
              />
            </Box>

            <Box mb={2}>
              <TextField
                id="contact-message"
                name="message"
                fullWidth
                label="הודעה"
                variant="outlined"
                multiline
                minRows={4}
                value={message}
                required
                autoComplete="off"
                error={
                  Boolean(
                    errors.message
                  )
                }
                helperText={
                  errors.message ||
                  " "
                }
                inputProps={{
                  "aria-invalid":
                    Boolean(
                      errors.message
                    ),

                  "aria-describedby":
                    errors.message
                      ? "contact-message-helper-text"
                      : undefined,
                }}
                onChange={(
                  event
                ) => {
                  dispatch(
                    setMessage(
                      event.target
                        .value
                    )
                  );

                  clearFieldError(
                    "message"
                  );
                }}
                sx={fieldStyles}
              />
            </Box>

            <Box
              minHeight={56}
              mb={2}
              aria-live="assertive"
            >
              {submissionError && (
                <Alert
                  id="contact-submission-error"
                  severity="error"
                  role="alert"
                >
                  {submissionError}
                </Alert>
              )}
            </Box>

            <Box
              display="flex"
              justifyContent="center"
            >
              <Button
                variant="contained"
                type="submit"
                disabled={
                  isSubmitting
                }
                aria-busy={
                  isSubmitting
                }
                sx={{
                  minWidth: 120,
                  minHeight: 44,
                  backgroundColor:
                    "#D81B60",

                  "&:hover": {
                    backgroundColor:
                      "#ad1457",
                  },

                  "&:focus-visible": {
                    outline:
                      "3px solid #000000",
                    outlineOffset:
                      "3px",
                  },
                }}
              >
                {isSubmitting
                  ? "שולח..."
                  : "שלח"}
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Container>
  );
}